import { resolveClientConfiguration } from "./client.configuration.ts";
import { createConfigurationError, createInvalidResponseError, FormbrewError } from "./errors.ts";
import { isDataEnvelope, isPublicFormDefinition, isSubmissionResult } from "./guards.ts";
import {
  assertSuccessfulResponse,
  createRequestHeaders,
  performRequest,
  readResponsePayload,
} from "./http.ts";
import { isRecord } from "./predicates.ts";
import type {
  FormbrewClientOptions,
  FormbrewRequestOptions,
  FormbrewSubmissionMetadata,
  FormbrewSubmissionResult,
  FormbrewSubmissionValues,
  FormbrewSubmitOptions,
  PublicFormDefinition,
} from "./types.ts";

export type FormbrewClient = {
  /** Fetches and validates the public form definition. */
  fetchForm(options?: FormbrewRequestOptions): Promise<PublicFormDefinition>;
  /** Submits direct field values. The SDK creates the HTTP submission envelope. */
  submit(
    values: FormbrewSubmissionValues,
    options?: FormbrewSubmitOptions,
  ): Promise<FormbrewSubmissionResult>;
};

type SubmissionEnvelope = {
  metadata?: FormbrewSubmissionMetadata;
  values: FormbrewSubmissionValues;
};

/** Creates a reusable client for one public Formbrew form token. */
export const createFormbrewClient = (options: FormbrewClientOptions): FormbrewClient => {
  const { baseUrl, fetchImplementation, origin, token } = resolveClientConfiguration(options);
  const manifestUrl = `${baseUrl}/forms/${encodeURIComponent(token)}`;

  const fetchForm = async (requestOptions: FormbrewRequestOptions = {}) => {
    const response = await performRequest(fetchImplementation, manifestUrl, {
      headers: createRequestHeaders(origin),
      method: "GET",
      signal: requestOptions.signal,
    });

    const payload = await readResponsePayload(response, requestOptions.signal);
    assertSuccessfulResponse(response, payload);

    if (!isDataEnvelope(payload) || !isPublicFormDefinition(payload.data)) {
      throw createInvalidResponseError(response.status);
    }

    return payload.data;
  };

  const submit = async (
    values: FormbrewSubmissionValues,
    submitOptions: FormbrewSubmitOptions = {},
  ) => {
    validateSubmissionInput(values, submitOptions.metadata);

    const envelope: SubmissionEnvelope = { values };
    if (submitOptions.metadata !== undefined) {
      envelope.metadata = submitOptions.metadata;
    }

    const body = serializeSubmission(envelope);
    const response = await performRequest(fetchImplementation, `${manifestUrl}/submit`, {
      body,
      headers: createRequestHeaders(origin, true),
      method: "POST",
      signal: submitOptions.signal,
    });

    const payload = await readResponsePayload(response, submitOptions.signal);
    assertSuccessfulResponse(response, payload);

    if (!isDataEnvelope(payload) || !isSubmissionResult(payload.data)) {
      throw createInvalidResponseError(response.status);
    }

    return payload.data;
  };

  return { fetchForm, submit };
};

const validateSubmissionInput = (
  values: FormbrewSubmissionValues,
  metadata: FormbrewSubmissionMetadata | undefined,
): void => {
  if (!isRecord(values)) {
    throw createConfigurationError("Submission values must be an object.");
  }

  if (metadata !== undefined && !isRecord(metadata)) {
    throw createConfigurationError("Submission metadata must be an object.");
  }
};

const serializeSubmission = (value: SubmissionEnvelope): string => {
  try {
    return JSON.stringify(value);
  } catch (error) {
    throw new FormbrewError("Submission values must be JSON serializable.", {
      cause: error,
      code: "ConfigurationError",
    });
  }
};
